import { useEffect } from "react";
import NewUI from "@/app/Router";
import GoogleAnalytics from "@/components/GoogleAnalytics";
import { useTrackContext } from "@/state/TrackContext";
import { CREATE_CART } from "@/lib/shopify/queries";
import { query } from "@/lib";
import { fetchAndDispatchMetaObjectConfig } from "@/shared/utils/metaobjectOperations";
import { fetchDayTypeIntervals } from "@/shared/utils/dayTypeOperations";

interface IProps {
  componentType?: string;
  hostedAt?: string;
  isCustomerLogged?: boolean;
  isCustomerActiveSubscriber?: boolean;
  customerPostalCode?: string;
  bookingConfig?: string;
}

const App = (props: IProps) => {
  const { componentType, hostedAt, isCustomerLogged, isCustomerActiveSubscriber, customerPostalCode } = props;

  const { dispatch, headlessCartInfo, membershipInfo } = useTrackContext();

  // Resolve the booking configuration once on mount and push it into context
  // state. Source priority is handled inside fetchAndDispatchMetaObjectConfig:
  // the inline data-booking-config attribute on #react-product is used first,
  // and the Shopify metaobject is fetched only as a fallback.
  useEffect(() => {
    fetchAndDispatchMetaObjectConfig(dispatch).catch((error) => {
      console.error("Failed to initialize booking configuration:", error);
    });
  }, [dispatch]);

  // Holiday ranges decide which days run the Sprint layout (calendar + heading).
  useEffect(() => {
    fetchDayTypeIntervals().then((dayTypeIntervals) => {
      dispatch({ type: "SET_DAY_TYPE_INTERVALS", payload: { dayTypeIntervals } });
    });
  }, [dispatch]);

  const styles = {
    // Font comes from the theme: styling-config-css.liquid sets --booking-font-body
    // on #react-product from the details-12 page template settings.
    fontFamily: "var(--booking-font-body, var(--font-body-family, sans-serif))",
    // Isolate so the animated background's negative z-index layers behind the
    // app content (and above the black fallback) without escaping this subtree.
    isolation: "isolate" as const,
  };

  const detectHostName = (url: string): string => {
    const urlObject = new URL(url);
    const hostParts = urlObject.host.split(".");

    if (hostParts.length >= 3 && hostParts[hostParts.length - 2] === "vercel") {
      // If the host has more than two parts and the second-to-last part is 'vercel'
      const subdomainParts = hostParts.slice(0, -2); // Exclude 'vercel' and the top-level domain
      return subdomainParts.join("-"); // Join the remaining parts with hyphens
    } else {
      // Otherwise, use a simplified logic for other cases
      const subdomain = hostParts[0].startsWith("www") ? hostParts[1] : hostParts[0];
      return subdomain || "";
    }
  };

  const hostName = detectHostName(window.location.href);
  // Create Cart
  useEffect(() => {
    (async () => {
      if (componentType !== "") {
        dispatch({
          type: "SET_PRODUCT_RENDER_LOCATION",
          payload: {
            productRenderedLocation: componentType as string,
          },
        });
      }

      if (hostedAt && hostedAt === "bosshunting") {
        dispatch({
          type: "SET_APP_HOSTED_AT",
          payload: {
            appHostedAt: hostedAt,
          },
        });
      }

      if (componentType !== "" && componentType === "external" && headlessCartInfo.id === "" && headlessCartInfo.checkoutUrl === "") {
        const cartCreated = await query(CREATE_CART, {
          input: {
            attributes: [{ key: "__source", value: hostName }],
          },
        });
        const { checkoutUrl, id } = cartCreated?.data?.cartCreate?.cart;
        if (checkoutUrl && id) {
          dispatch({
            type: "SET_CART_INFORMATION",
            payload: {
              cart: {
                id,
                checkoutUrl,
              },
            },
          });
        }
      }

      if (isCustomerLogged || isCustomerActiveSubscriber || customerPostalCode) {
        dispatch({
          type: "SET_IS_CUSTOMER_LOGGED_IN",
          payload: {
            customerInfo: {
              isCustomerLogged: Boolean(isCustomerLogged),
              isCustomerActiveSubscriber: Boolean(isCustomerActiveSubscriber),
              customerPostalCode: customerPostalCode || "",
            },
          },
        });

        // Only dispatch when the desired value actually differs from the
        // current state. Without this guard, logged-in non-subscribers
        // (where `Boolean(isCustomerActiveSubscriber)` is false and the
        // initial `isMembershipItemAddedToCart` is also false) would
        // dispatch a no-op SET_MEMBERSHIP_INFO on every render — the
        // reducer always produces a new object reference, which retriggers
        // this effect via the `membershipInfo` dependency, creating an
        // infinite render loop that makes the membership popup feel
        // unresponsive (can't dismiss / popup not closing after Subscribe).
        const desiredMembershipState = Boolean(isCustomerActiveSubscriber);
        if (membershipInfo.isMembershipItemAddedToCart !== desiredMembershipState) {
          dispatch({
            type: "SET_MEMBERSHIP_INFO",
            payload: {
              membershipInfo: {
                ...membershipInfo,
                isMembershipItemAddedToCart: desiredMembershipState,
              },
            },
          });
        }
      }
    })();
  // Only depend on the specific membershipInfo field this effect checks,
  // not the entire object — dispatching SET_MEMBERSHIP_INFO would otherwise
  // create a new object reference and re-trigger this effect unnecessarily.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, componentType, hostName, hostedAt, isCustomerLogged, isCustomerActiveSubscriber, headlessCartInfo.id, headlessCartInfo.checkoutUrl, customerPostalCode, membershipInfo.isMembershipItemAddedToCart]);

  return (
    <div className="tw-relative tw-bg-black" style={styles}>
      <GoogleAnalytics />
      <NewUI />
    </div>
  );
};

export default App;
